import React, { useContext, useEffect, useRef, useState } from "react";
import DraftsCredentialTemplateCard from "../../components/Cards/DraftsCredentialTemplateCard/DraftsCredentialTemplateCard";
import { GET, POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import AuthContext from "../../store/auth-context";
import CfLoader from "../../common/CfLoader/CfLoader";
import { useLocation, useNavigate } from "react-router-dom";
import CalculateCardsPerRow from "../../service/CalculateCardsPerRow";
import Toast from "../../common/Toast/Toast";
import ToolTip from "../../common/ToolTip/ToolTip";
import podcastIcon from "../../resources/icons/podcast-icon.svg";

const AddDrafts = ({ programmeId, batchId, onClose }) => {
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoadingCredTemplates, setIsLoadingCredTemplates] = useState(false);
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);
  const [cardWidth] = useState("19.125rem");
  const [gap] = useState("1.5rem");
  const containerRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(0);
  const [countRecentList, setCountRecentList] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [recentList, setRecentList] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [offset, setOffset] = useState(0);
  const [isPaginating, setIsPaginating] = useState(false);
  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false); // New state to track initial load

  const SEARCH = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    if (searchTerm.trim() === "") {
      setIsSearching(false);
      setSearchResults([]);
      setSearchCompleted(false);
      setOffset(0);
      GET_ISSUER_RECENT(cardsPerRow, 0);
      return;
    }
    setIsSearching(true);
    setSearchCompleted(false);
    setOffset(0);
    GET(
      `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}/new?batch_code=${batchId}&limit=${cardsPerRow}&offset=0&search=${searchTerm}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.log(data.error);
          setIsSearching(false);
          setSearchResults([]);
        } else {
          setSearchResults(data.data.results);
          setCountRecentList(data.data.count);
          setNext(data.data.next);
          setPrevious(data.data.previous);
        }
        setSearchCompleted(true);
      })
      .catch((error) => {
        console.error(error);
        setIsSearching(false);
        setSearchResults([]);
        setSearchCompleted(true);
      });
  };

  const GET_ISSUER_RECENT = (limit, offset) => {
    setIsLoadingCredTemplates(true);
    setIsPaginating(true);
    GET(
      `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}/new?batch_code=${batchId}&limit=${limit}&offset=${offset}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.log(data.error);
        } else {
          setRecentList(data.data.results);
          setCountRecentList(data.data.count);
          setNext(data.data.next);
          setPrevious(data.data.previous);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoadingCredTemplates(false);
        setIsPaginating(false);
        setInitialLoadCompleted(true);
      });
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(
      templateId === selectedTemplateId ? null : templateId
    );
  };

  const handleTemplatesClick = () => {
    navigate("/discovery");
  };

  const handleAddTemplate = () => {
    if (!selectedTemplateId) return;

    setIsAddingTemplate(true);
    const payload = {
      issuer_template_id: selectedTemplateId,
      programme_code: programmeId,
      batch_code: batchId,
    };

    POST(
      `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}/new`,
      payload
    )
      .then((response) => {
        if (!response.ok) {
          console.log("Failed to add template");
          Toast({
            type: "error",
            title: "Error",
            message: "Failed to add template",
          });
        }
        return response.json();
      })
      .then(() => {
        Toast({
          type: "success",
          title: "Success",
          message: "Template added successfully",
        });
        setSelectedTemplateId(null);
        onClose();
      })
      .catch((error) => {
        console.error("Error adding template:", error);
      })
      .finally(() => {
        setIsAddingTemplate(false);
      });
  };

  const handlePrevious = () => {
    const newOffset = offset - cardsPerRow;
    if (newOffset >= 0) {
      setOffset(newOffset);
      if (isSearching) {
        const searchTerm = document.getElementById("search").value;
        SEARCH({ target: { value: searchTerm } });
      } else {
        GET_ISSUER_RECENT(cardsPerRow, newOffset);
      }
    }
  };

  const handleNext = () => {
    const newOffset = offset + cardsPerRow;
    setOffset(newOffset);
    if (isSearching) {
      const searchTerm = document.getElementById("search").value;
      SEARCH({ target: { value: searchTerm } });
    } else {
      GET_ISSUER_RECENT(cardsPerRow, newOffset);
    }
  };

  const removeCredentialFromList = (credentialId) => {
    const removeAndEnsure = (list, setList, startDay, endDay, count) => {
      setList((prev) => {
        const updatedList = prev.filter(
          (item) => item.issuer_credential_template_id !== credentialId
        );
        ensureFullRow(updatedList, setList, startDay, endDay, count);
        return updatedList;
      });
    };
    const ensureFullRow = (list, setList, startDay, endDay, count) => {
      if (list.length < cardsPerRow && count > list.length) {
        const offset = list.length;
        GET(
          `${EntityConstants.GET_DRAFTS_TEMPLATES}limit=${
            cardsPerRow - list.length
          }&offset=${offset}&start_day=${startDay}&end_day=${endDay}`
        )
          .then((response) => response.json())
          .then((data) => {
            if (!data.error && data.results.length > 0) {
              setList((prev) => [...prev, ...data.results]);
            }
          })
          .catch((error) => {
            console.error("Error fetching additional certificates:", error);
          });
      }
    };
    removeAndEnsure(searchResults, setSearchResults);
    removeAndEnsure(recentList, setRecentList, 0, 7, countRecentList);
  };

  const nextButtonWithTooltip = (
    <ToolTip
      component={
        <button
          onClick={handleNext}
          disabled={next === null || selectedTemplateId}
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
        >
          <span className="flex items-center justify-center gap-2">
            <span>Next</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
              />
            </svg>
          </span>
        </button>
      }
      message={
        selectedTemplateId
          ? "Please deselect the certificate to navigate"
          : next === null
          ? "No next page available"
          : ""
      }
      tooltipPosition="top-[-4rem]"
      bgColor="#6B7280"
      tooltipWidth="w-40"
    />
  );

  const previousButtonWithTooltip = (
    <ToolTip
      component={
        <button
          onClick={handlePrevious}
          disabled={previous === null || selectedTemplateId}
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
        >
          <span className="flex items-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
              />
            </svg>
            <span>previous</span>
          </span>
        </button>
      }
      message={
        selectedTemplateId
          ? "Please deselect the certificate to navigate"
          : previous === null
          ? "No previous page available"
          : ""
      }
      tooltipPosition="top-[-4rem]"
      bgColor="#6B7280"
      tooltipWidth="w-40"
    />
  );

  const nextButtonNoTooltip = (
    <button
      onClick={handleNext}
      disabled={next === null || selectedTemplateId}
      className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
    >
      <span className="flex items-center justify-center gap-2">
        <span>Next</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-5 h-5 rtl:-scale-x-100"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
          />
        </svg>
      </span>
    </button>
  );

  const previousButtonNoTooltip = (
    <button
      onClick={handlePrevious}
      disabled={previous === null || selectedTemplateId}
      className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
    >
      <span className="flex items-center justify-center gap-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-5 h-5 rtl:-scale-x-100"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
          />
        </svg>
        <span>previous</span>
      </span>
    </button>
  );

  useEffect(() => {
    const updateCardsPerRow = () => {
      if (containerRef.current) {
        const calculatedCardsPerRow = CalculateCardsPerRow(
          containerRef,
          cardWidth,
          gap
        );
        if (calculatedCardsPerRow < 5) {
          setCardsPerRow(6);
        } else {
          setCardsPerRow(calculatedCardsPerRow);
        }
      }
    };

    updateCardsPerRow();
  }, [cardWidth, gap]);

  useEffect(() => {
    if (cardsPerRow > 0) {
      GET_ISSUER_RECENT(cardsPerRow, 0);
    }
  }, [cardsPerRow]);

  useEffect(() => {
    authContext.lastVisitedPathHandler(location.pathname);
  }, []);

  const showEmptyState =
    initialLoadCompleted &&
    !isSearching &&
    recentList.length === 0 &&
    !isLoadingCredTemplates;

  return (
    <div className="flex flex-col items-center w-full bg-cfSecondary min-h-screen">
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4 top-0 sticky z-20`}
      >
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
        >
          <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg">
            <p>Existing Certificates</p>
          </div>
          <div className="flex gap-2">
            {selectedTemplateId && (
              <button
                onClick={handleAddTemplate}
                disabled={isAddingTemplate}
                className={`px-4 py-2 bg-[#3538CD] text-white font-medium rounded-md ${
                  isAddingTemplate ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isAddingTemplate ? "Processing..." : "Add Certificate"}
              </button>
            )}
            {/* Search */}
            <form
              onSubmit={(e) => e.preventDefault()}
              className={`bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] self-center md:self-end md:m-0`}
            >
              <input
                type="text"
                name="search"
                id="search"
                onChange={SEARCH}
                placeholder="Search Certificate"
                className="outline-none border-none p-0 placeholder:text-sm w-full"
                style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
              />
            </form>
          </div>
        </div>
      </div>

      <div ref={containerRef} className="w-full px-4 pb-4">
        {showEmptyState && (
          <div className="flex flex-col flex-wrap sm:mt-0 xl:mt-6 w-full bg-white rounded-2xl">
            <div className="flex flex-col px-2 lg:px-0 mt-20 md:mt-36 justify-center items-center gap-5">
              <img
                alt="certificate-icon"
                src={podcastIcon}
                height="80px"
                width="80px"
                className=""
              />
              <h1 className="text-cfHeadingText font-semibold text-center">
                No Certificates Available
              </h1>
              <h3 className="text-xs font-bold text-center lg:text-md text-gray-500">
                You haven’t created or issued any certificates.
                <br />
                Start by choosing a template to create your first certificate.
              </h3>
              <button
                className="rounded-[8px] items-center px-4 py-2.5 bg-cfPrimaryButton text-white font-medium lg:font-semibold text-sm"
                onClick={handleTemplatesClick}
              >
                See Templates
              </button>
            </div>
          </div>
        )}

        {isSearching && (
          <div className="flex flex-col">
            <div className="flex items-center gap-6 flex-wrap w-full mt-8">
              {searchResults.map(
                ({
                  issuer_credential_template_id,
                  credential_template_id,
                  certificate_name,
                  preview_image,
                  thumbnail_webp_url,
                  created_at,
                  updated_at,
                  description,
                  tagsList,
                  template_type,
                }) => (
                  <DraftsCredentialTemplateCard
                    key={issuer_credential_template_id}
                    name={certificate_name}
                    credential_template_id={credential_template_id}
                    issuer_credential_template_id={
                      issuer_credential_template_id
                    }
                    created_at={created_at}
                    lastEdited={updated_at}
                    thumbnail_image={preview_image}
                    thumbnail_webp_url={thumbnail_webp_url}
                    description={description}
                    tagsList={tagsList}
                    removeCredentialFromList={removeCredentialFromList}
                    removeActionButtons={true}
                    isSelected={
                      selectedTemplateId === issuer_credential_template_id
                    }
                    onSelect={handleTemplateSelect}
                    template_type={template_type}
                  />
                )
              )}
              {isSearching && !searchCompleted && (
                <div className="w-full flex justify-center items-center py-4">
                  <CfLoader />
                </div>
              )}
              {searchCompleted && isSearching && searchResults.length === 0 && (
                <div className="mt-6 w-full flex justify-center">
                  <p className="text-lg font-semibold text-gray-600">
                    Certificate not found
                  </p>
                </div>
              )}
            </div>
            {searchResults.length > 0 && (
              <div className="flex items-center justify-between mt-6 w-full">
                {selectedTemplateId || previous === null
                  ? previousButtonWithTooltip
                  : previousButtonNoTooltip}
                {selectedTemplateId || next === null
                  ? nextButtonWithTooltip
                  : nextButtonNoTooltip}
              </div>
            )}
          </div>
        )}

        {!isSearching && !showEmptyState && (
          <>
            {recentList[0] && (
              <div className="flex justify-between my-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <p className="text-cfHeadingText font-medium text-lg">
                    Please <span className="text-[#3538CD]">select one</span>{" "}
                    certificate template to issue
                  </p>
                </div>
              </div>
            )}
            <div className="flex flex-col">
              <div className="flex items-center gap-6 flex-wrap w-full">
                {isPaginating ? (
                  <div className="w-full flex justify-center items-center py-[18rem]">
                    <CfLoader />
                  </div>
                ) : (
                  <>
                    {recentList.map(
                      ({
                        issuer_credential_template_id,
                        credential_template_id,
                        certificate_name,
                        preview_image,
                        thumbnail_webp_url,
                        created_at,
                        updated_at,
                        description,
                        tagsList,
                        template_type,
                        programme_name,
                        batch_name,
                      }) => (
                        <DraftsCredentialTemplateCard
                          key={issuer_credential_template_id}
                          name={certificate_name}
                          credential_template_id={credential_template_id}
                          issuer_credential_template_id={
                            issuer_credential_template_id
                          }
                          created_at={created_at}
                          //! remove this when `updated_at` is added 'lastEdited={created_at?created_at:"14 Jan 2024"}'
                          lastEdited={updated_at}
                          thumbnail_image={preview_image}
                          thumbnail_webp_url={thumbnail_webp_url}
                          description={description}
                          tagsList={tagsList}
                          removeCredentialFromList={removeCredentialFromList}
                          removeActionButtons={true}
                          isSelected={
                            selectedTemplateId === issuer_credential_template_id
                          }
                          onSelect={handleTemplateSelect}
                          template_type={template_type}
                          programme_name={programme_name}
                          batch_name={batch_name}
                        />
                      )
                    )}
                    {/* Fix loader positioning - show only when loading and no certificates */}
                    {isLoadingCredTemplates && recentList.length === 0 && (
                      <div className="w-full flex justify-center items-center py-[18rem]">
                        <CfLoader />
                      </div>
                    )}
                  </>
                )}
              </div>
              {recentList.length > 0 && (
                <div className="flex items-center justify-between mt-6 w-full">
                  {selectedTemplateId || previous === null
                    ? previousButtonWithTooltip
                    : previousButtonNoTooltip}
                  {selectedTemplateId || next === null
                    ? nextButtonWithTooltip
                    : nextButtonNoTooltip}
                </div>
              )}
            </div>
            {recentList.length === 0 &&
              !isSearching &&
              !isLoadingCredTemplates && (
                <div className="mt-6 w-full flex justify-center">
                  <p className="text-lg font-semibold text-gray-600">
                    No certificates found
                  </p>
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default AddDrafts;
